import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Shield,
  Users,
  Truck,
  Clock,
  CheckCircle,
  ChevronRight,
  FileText,
  Phone,
  Target,
  Globe,
  AlertTriangle,
  Building2,
  Plane,
  MapPin,
  Radio,
  ClipboardList,
  Zap,
  Lock,
  Eye,
  Compass,
  Package,
  Headphones,
} from "lucide-react";

const MissionSupportPage = () => {
  // Service Pillars
  const servicePillars = [
    {
      id: "security",
      icon: Shield,
      title: "Protective Security & High-Risk Support",
      description: "Tier 1–standard operational discipline for facility protection, executive security coordination, and critical asset protection.",
      services: [
        "Facility security augmentation",
        "Executive/VIP transport security coordination",
        "Government site security support",
        "Airfield & port security augmentation",
        "Critical asset protection",
        "Surge security teams",
      ],
    },
    {
      id: "dod",
      icon: Target,
      title: "DoD Mission Support & Expeditionary Operations",
      description: "Forward-deployed logistics and personnel support for defense contractors and government operations requiring rapid mobilization.",
      services: [
        "Forward logistics support",
        "Base support augmentation",
        "Airfield/flightline support teams",
        "Rapid deployment personnel",
        "Secure transport coordination",
        "72-hour movement readiness support",
        "Contingency operations support",
      ],
    },
    {
      id: "fema",
      icon: AlertTriangle,
      title: "FEMA & Disaster Response Support",
      description: "Rapid-response teams and logistics coordination for emergency management agencies and disaster relief operations.",
      services: [
        "Disaster zone logistics",
        "Staging site setup",
        "Distribution site security",
        "Emergency transport coordination",
        "Rapid response personnel deployment",
        "Infrastructure stabilization support",
        "Continuity operations support",
      ],
    },
  ];

  // Key Capabilities
  const capabilities = {
    personnel: {
      title: "Personnel",
      icon: Users,
      items: [
        "Veteran-led teams",
        "Security support staff",
        "Expeditionary logistics teams",
        "Drivers & transport coordinators",
        "Site support crews",
      ],
    },
    operations: {
      title: "Operations",
      icon: Compass,
      items: [
        "Rapid mobilization model",
        "24/7 readiness posture",
        "Scalable staffing",
        "Prime contractor integration",
        "Multi-state deployment capability",
      ],
    },
    technology: {
      title: "Technology & Reporting",
      icon: ClipboardList,
      items: [
        "Incident reporting",
        "Chain-of-custody discipline",
        "Operational logs",
        "After-action reporting",
        "Accountability tracking",
      ],
    },
    logistics: {
      title: "Logistics",
      icon: Package,
      items: [
        "Staging & deployment coordination",
        "Equipment transport",
        "Temporary site setup",
        "Air/ground coordination",
        "Emergency support mobility",
      ],
    },
  };

  // Mobilization Steps
  const mobilizationSteps = [
    { step: "01", title: "Mission Intake", description: "Requirements review and scope definition" },
    { step: "02", title: "Readiness Assessment", description: "Rapid capability and resource evaluation" },
    { step: "03", title: "Team Assembly", description: "Personnel selection and credentialing" },
    { step: "04", title: "Staging & Planning", description: "Deployment coordination and logistics" },
    { step: "05", title: "Field Execution", description: "Mission delivery and real-time reporting" },
    { step: "06", title: "After-Action", description: "Documentation and support closeout" },
  ];

  // Compliance Standards
  const complianceStandards = [
    "FEMA operational support environments",
    "DoD support contractor environments",
    "Incident command structure familiarity",
    "Safety & risk management culture",
    "Documentation-first operations",
    "Subcontractor control discipline",
    "Professional conduct standards",
    "Operational reporting expectations",
  ];

  // Mission Scenarios
  const scenarios = [
    {
      title: "Hurricane Response Staging Support",
      description: "Rapid deployment of logistics personnel and equipment to support FEMA staging areas during hurricane response operations. Teams provide 24/7 site management, inventory control, and distribution coordination.",
    },
    {
      title: "Federal Facility Security Augmentation",
      description: "Surge security personnel deployment to federal facilities requiring enhanced protective posture. Veteran-led teams integrate with existing security infrastructure under facility command.",
    },
    {
      title: "Airfield Surge Operations",
      description: "Flightline support teams deployed to military or civilian airfields during high-tempo operations. Personnel provide ground handling, cargo coordination, and security support.",
    },
    {
      title: "FEMA Distribution Site Support",
      description: "Complete staffing and security for Points of Distribution (PODs) during disaster recovery operations. Teams manage crowd control, supply distribution, and site documentation.",
    },
    {
      title: "Convoy & Route Support Operations",
      description: "Secure transport coordination and escort services for sensitive cargo movement. Teams provide advance route reconnaissance, communication relay, and incident response capability.",
    },
    {
      title: "Forward Operations Site Setup",
      description: "Temporary site establishment for expeditionary operations requiring rapid infrastructure deployment. Teams manage site security, logistics flow, and operational support services.",
    },
  ];

  // Differentiators
  const differentiators = [
    { icon: Shield, text: "Veteran leadership with military operational discipline" },
    { icon: Zap, text: "72-hour rapid mobilization capability" },
    { icon: FileText, text: "Documentation-first operational culture" },
    { icon: Building2, text: "Government-ready compliance structure" },
    { icon: Users, text: "Prime contractor integration experience" },
    { icon: Compass, text: "Expeditionary mindset and forward-deployed capability" },
    { icon: Radio, text: "Disciplined communication and reporting rhythm" },
  ];

  // FAQ Items
  const faqItems = [
    {
      question: "How fast can you deploy?",
      answer: "AMR Global Holdings maintains a 72-hour rapid mobilization capability for most mission requirements. Initial assessment teams can deploy within 24-48 hours for urgent requirements. Deployment timelines are confirmed during the mission intake process based on scope, location, and personnel requirements.",
    },
    {
      question: "What is your geographic coverage?",
      answer: "We maintain multi-state deployment capability with headquarters at New Orleans Lakefront Airport (KNEW). Our network supports operations throughout the continental United States (CONUS) with expeditionary capability for OCONUS requirements through partner coordination.",
    },
    {
      question: "Do you provide armed or unarmed services?",
      answer: "AMR Global Holdings provides both armed and unarmed security support personnel based on mission requirements and jurisdictional regulations. All armed personnel maintain appropriate state licensing and credentials. Service configuration is determined during mission planning.",
    },
    {
      question: "Do you support FEMA missions?",
      answer: "Yes. We provide personnel, logistics, and security support for FEMA disaster response operations including staging site management, Points of Distribution (POD) staffing, and emergency transport coordination. Our teams are familiar with Incident Command System (ICS) structures.",
    },
    {
      question: "Do you support DoD contractors?",
      answer: "Yes. AMR Global Holdings provides subcontractor support to prime contractors executing DoD missions. We support forward logistics, base augmentation, airfield operations, and contingency support requirements with documentation and reporting that meets prime contractor standards.",
    },
    {
      question: "How do you integrate with prime contractors?",
      answer: "We operate as a disciplined subcontractor with established reporting protocols, credentialing processes, and operational documentation. Our teams integrate under prime contractor command structures and provide regular status reporting, incident documentation, and after-action deliverables.",
    },
    {
      question: "What reporting is provided?",
      answer: "All missions include comprehensive documentation: daily operational logs, incident reports, personnel accountability tracking, chain-of-custody records where applicable, and formal after-action reports. Reporting frequency and format are tailored to client requirements.",
    },
    {
      question: "How do I request support?",
      answer: "Contact our operations team via phone at 504-319-7683 or email at AMREID@AMRGLOBALHOLDINGS.COM. Initial consultations include mission requirements review, capability assessment, and preliminary timeline/staffing estimates. Request our Capability Statement for detailed company information.",
    },
  ];

  return (
    <div className="min-h-screen" data-testid="mission-support-page">
      {/* HERO SECTION */}
      <section
        className="relative min-h-[85vh] flex items-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1767491629923-009ffdc395d8?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="mission-hero"
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4 animate-fade-in-up">
              SECURITY • DISASTER RESPONSE • EXPEDITIONARY SUPPORT
            </p>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl text-white leading-tight mb-6 animate-fade-in-up animation-delay-100">
              MISSION-READY.<br />
              <span className="text-[#c5a04a]">DOCUMENTATION FIRST.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-200 mb-4 leading-relaxed animate-fade-in-up animation-delay-200">
              AMR Global Holdings LLC provides veteran-led security, disaster response, and 
              expeditionary operations support to government agencies and prime contractors 
              requiring disciplined, auditable execution.
            </p>
            <p className="text-slate-300 mb-8 animate-fade-in-up animation-delay-200">
              Rapid mobilization. Prime-contractor ready. Built for federal mission environments.
            </p>
            
            {/* Proof Points */}
            <div className="flex flex-wrap gap-6 mb-10 animate-fade-in-up animation-delay-300">
              <div className="flex items-center gap-2 text-white">
                <CheckCircle className="w-5 h-5 text-[#c5a04a]" />
                <span className="text-sm">Veteran-Owned Small Business</span>
              </div>
              <div className="flex items-center gap-2 text-white">
                <CheckCircle className="w-5 h-5 text-[#c5a04a]" />
                <span className="text-sm">72-Hour Mobilization Capability</span>
              </div>
              <div className="flex items-center gap-2 text-white">
                <CheckCircle className="w-5 h-5 text-[#c5a04a]" />
                <span className="text-sm">Prime Contractor Integration Ready</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-400">
              <Link to="/contact?type=capability_statement">
                <Button className="btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm" data-testid="hero-capability-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Request Capability Statement
                </Button>
              </Link>
              <Link to="/contact?type=contracting">
                <Button variant="outline" className="btn-tactical border-2 border-white text-white hover:bg-white hover:text-[#1a365d] text-sm" data-testid="hero-call-btn">
                  <Phone className="w-4 h-4 mr-2" />
                  Schedule Readiness Call
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE PILLARS */}
      <section className="py-20 bg-white" data-testid="service-pillars">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">PRIMARY CAPABILITIES</p>
            <h2 className="font-heading text-3xl md:text-5xl text-[#1a365d]">SERVICE PILLARS</h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {servicePillars.map((pillar, index) => (
              <Card key={pillar.id} className="card-tactical h-full" data-testid={`pillar-${pillar.id}`}>
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-[#1a365d] rounded-sm flex items-center justify-center mb-6">
                    <pillar.icon className="w-8 h-8 text-[#c5a04a]" />
                  </div>
                  <h3 className="font-heading text-xl text-[#1a365d] mb-4">{pillar.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">{pillar.description}</p>
                  <ul className="space-y-3">
                    {pillar.services.map((service, sIndex) => (
                      <li key={sIndex} className="flex items-start gap-3">
                        <CheckCircle className="w-4 h-4 text-[#c5a04a] flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600 text-sm">{service}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* KEY CAPABILITIES */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="key-capabilities">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">RESOURCES</p>
            <h2 className="font-heading text-3xl md:text-5xl text-[#1a365d]">KEY CAPABILITIES</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.values(capabilities).map((cap, index) => (
              <Card key={index} className="card-tactical" data-testid={`capability-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#1a365d] rounded-sm flex items-center justify-center">
                      <cap.icon className="w-5 h-5 text-[#c5a04a]" />
                    </div>
                    <h3 className="font-heading text-lg text-[#1a365d]">{cap.title}</h3>
                  </div>
                  <ul className="space-y-2">
                    {cap.items.map((item, iIndex) => (
                      <li key={iIndex} className="text-slate-600 text-sm flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-[#c5a04a] rounded-full mt-2 flex-shrink-0"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE MOBILIZE */}
      <section className="py-20 bg-[#1a365d]" data-testid="mobilization-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">PROCESS</p>
            <h2 className="font-heading text-3xl md:text-5xl text-white">HOW WE MOBILIZE</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mobilizationSteps.map((step, index) => (
              <div
                key={index}
                className="p-6 border border-slate-700 rounded-sm hover:border-[#c5a04a] transition-colors relative"
                data-testid={`mobilization-step-${index}`}
              >
                <span className="text-6xl font-heading text-[#c5a04a] opacity-20 absolute top-2 right-4">
                  {step.step}
                </span>
                <h3 className="font-heading text-xl text-white mb-2 relative z-10">{step.title}</h3>
                <p className="text-slate-300 text-sm relative z-10">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE & STANDARDS */}
      <section className="py-20 bg-white" data-testid="compliance-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">ALIGNMENT</p>
              <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d] mb-6">
                COMPLIANCE & STANDARDS
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                AMR Global Holdings operates with documentation-first discipline aligned with 
                federal operational environments. Our processes are structured for integration 
                with government agencies and prime contractor requirements.
              </p>
              <div className="grid grid-cols-1 gap-3">
                {complianceStandards.map((standard, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-sm">
                    <Lock className="w-4 h-4 text-[#c5a04a] flex-shrink-0" />
                    <span className="text-slate-700 text-sm">Aligned with {standard}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#1a365d] p-8 rounded-sm">
                <Clock className="w-10 h-10 text-[#c5a04a] mb-4" />
                <p className="text-4xl font-heading text-white mb-2">72</p>
                <p className="text-slate-300 text-sm tracking-wider">HOUR MOBILIZATION</p>
              </div>
              <div className="bg-[#c5a04a] p-8 rounded-sm">
                <Shield className="w-10 h-10 text-[#1a365d] mb-4" />
                <p className="text-4xl font-heading text-[#1a365d] mb-2">24/7</p>
                <p className="text-[#1a365d] text-sm tracking-wider">READINESS POSTURE</p>
              </div>
              <div className="col-span-2 bg-slate-100 p-8 rounded-sm">
                <FileText className="w-10 h-10 text-[#1a365d] mb-4" />
                <p className="text-2xl font-heading text-[#1a365d] mb-2">DOCUMENTATION FIRST</p>
                <p className="text-slate-600 text-sm">Every mission includes comprehensive reporting and after-action documentation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION SCENARIOS */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="mission-scenarios">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">APPLICATIONS</p>
            <h2 className="font-heading text-3xl md:text-5xl text-[#1a365d]">MISSION SCENARIOS</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {scenarios.map((scenario, index) => (
              <Card key={index} className="card-tactical" data-testid={`scenario-${index}`}>
                <CardContent className="p-6">
                  <div className="w-8 h-8 bg-[#c5a04a] rounded-sm flex items-center justify-center mb-4">
                    <span className="font-heading text-[#1a365d] text-sm">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="font-heading text-lg text-[#1a365d] mb-3">{scenario.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{scenario.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="py-20 bg-[#1a365d]" data-testid="differentiators">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">ADVANTAGE</p>
            <h2 className="font-heading text-3xl md:text-5xl text-white">WHY AMR GLOBAL</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {differentiators.map((diff, index) => (
              <div
                key={index}
                className="p-6 border border-slate-700 rounded-sm hover:border-[#c5a04a] transition-colors text-center"
                data-testid={`diff-${index}`}
              >
                <diff.icon className="w-10 h-10 text-[#c5a04a] mx-auto mb-4" />
                <p className="text-white text-sm leading-relaxed">{diff.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-20 bg-white" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">QUESTIONS</p>
            <h2 className="font-heading text-3xl md:text-5xl text-[#1a365d]">FREQUENTLY ASKED</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border border-slate-200 rounded-sm px-6"
                data-testid={`faq-${index}`}
              >
                <AccordionTrigger className="text-left font-heading text-[#1a365d] hover:text-[#c5a04a] py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm leading-relaxed pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section
        className="py-20 relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1600627290586-557423bbe623?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="final-cta"
      >
        <div className="absolute inset-0 bg-[#1a365d]/95"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-5xl text-white mb-4">
            READY WHEN YOU ARE.
          </h2>
          <p className="text-slate-200 text-lg mb-8">
            Contact AMR Global Holdings LLC to discuss your mission support requirements.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Link to="/contact?type=capability_statement">
              <Button className="btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm" data-testid="cta-capability-btn">
                <FileText className="w-4 h-4 mr-2" />
                Request Capability Statement
              </Button>
            </Link>
            <Link to="/contact?type=contracting">
              <Button variant="outline" className="btn-tactical border-2 border-white text-white hover:bg-white hover:text-[#1a365d] text-sm" data-testid="cta-call-btn">
                <Phone className="w-4 h-4 mr-2" />
                Schedule Readiness Call
              </Button>
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-8 text-slate-300 text-sm">
            <a href="tel:504-319-7683" className="flex items-center gap-2 hover:text-[#c5a04a] transition-colors">
              <Phone className="w-4 h-4" />
              504-319-7683
            </a>
            <a href="mailto:AMREID@AMRGLOBALHOLDINGS.COM" className="flex items-center gap-2 hover:text-[#c5a04a] transition-colors">
              <Headphones className="w-4 h-4" />
              AMREID@AMRGLOBALHOLDINGS.COM
            </a>
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#c5a04a]" />
              New Orleans Lakefront Airport (KNEW)
            </span>
          </div>
        </div>
      </section>

      {/* CAPABILITY STATEMENT SUMMARY */}
      <section className="py-16 bg-slate-100" data-testid="capability-summary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-sm border-l-4 border-[#c5a04a] shadow-sm">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-xs mb-4">CAPABILITY STATEMENT SUMMARY</p>
            <p className="text-slate-700 leading-relaxed">
              AMR Global Holdings LLC is a Veteran-Owned Small Business providing security, disaster response, 
              and expeditionary operations support to government agencies and prime contractors. Headquartered 
              at New Orleans Lakefront Airport (KNEW), we maintain 72-hour rapid mobilization capability with 
              veteran-led teams trained in Tier 1–standard operational discipline. Our documentation-first 
              approach ensures auditable execution aligned with DoD, FEMA, and DHS operational environments. 
              Services include protective security augmentation, forward logistics support, disaster zone 
              operations, and contingency personnel deployment. SAM.gov registered, CAGE Code active, 
              HUBZone-aligned positioning.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MissionSupportPage;
